import React from 'react';
import { Link, graphql, useStaticQuery } from 'gatsby';
import Img from 'gatsby-image';

import FbIcon from '../svgs/socials-fill/facebook.inline.svg';
import InstaIcon from '../svgs/socials-fill/instagram.inline.svg';
import WaIcon from '../svgs/socials-fill/whatsapp.inline.svg';

const footerQuery = graphql`
  {
    sanityContactSocialSection {
      facebookUrl
      instagramUrl
      whatsappUrl
    }
    sanityServicesSection {
      certTitle
      certLogo {
        asset {
          _id
          url
        }
      }
    }
    logo: file(relativePath: { eq: "logo.png" }) {
      childImageSharp {
        fixed(width: 170) {
          ...GatsbyImageSharpFixed
        }
      }
    }
  }
`;

export default function Footer() {
  const data = useStaticQuery(footerQuery);
  const social = data?.sanityContactSocialSection || {};
  const { facebookUrl, instagramUrl, whatsappUrl } = social;
  const certTitle = data?.sanityServicesSection?.certTitle;
  // Falls back to the bundled CIDB logo until a Certification Logo is set
  // on the "Our Services" document in the CMS.
  const certLogoUrl =
    data?.sanityServicesSection?.certLogo?.asset?.url || '/cidb-logo.png';

  const footerLinks = [
    { to: '/', label: 'Home' },
    { to: '/services', label: 'Services' },
    { to: '/portfolio', label: 'Portfolio' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <footer style={{ background: '#ffffff' }}>
      <div
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '60px 24px 40px',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '40px',
          }}
          className="grid-footer"
        >
          {/* Brand */}
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '14px' }}>
              <Img
                fixed={data.logo.childImageSharp.fixed}
                alt="Decorzo Space"
              />
            </div>
            <p
              style={{
                fontSize: '14px',
                color: 'rgba(0,0,0,0.65)',
                lineHeight: '1.7',
                whiteSpace: 'nowrap',
              }}
            >
              Your trusted partner in home renovation and interior design.
            </p>
          </div>

          {/* Quick Links */}
          <div style={{ paddingLeft: '40px' }} className="footer-quicklinks">
            <h4
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '13px',
                fontWeight: 600,
                color: '#1b2733',
                marginBottom: '20px',
              }}
            >
              Quick Links
            </h4>
            <div>
              {footerLinks.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    color: 'rgba(0,0,0,0.7)',
                    marginBottom: '10px',
                    transition: 'color 0.3s ease',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={e => (e.target.style.color = '#F08020')}
                  onMouseLeave={e => (e.target.style.color = 'rgba(0,0,0,0.7)')}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Social */}
          {(whatsappUrl || facebookUrl || instagramUrl) && (
            <div>
              <h4
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#1b2733',
                  marginBottom: '20px',
                }}
              >
                Follow Us
              </h4>
              <div style={{ display: 'flex', gap: '10px' }}>
                {whatsappUrl && (
                  <a
                    className="social-brand whatsapp"
                    target="_blank"
                    rel="noreferrer"
                    href={whatsappUrl}
                    aria-label="WhatsApp"
                  >
                    <WaIcon />
                  </a>
                )}
                {facebookUrl && (
                  <a
                    className="social-brand facebook"
                    target="_blank"
                    rel="noreferrer"
                    href={facebookUrl}
                    aria-label="Facebook"
                  >
                    <FbIcon />
                  </a>
                )}
                {instagramUrl && (
                  <a
                    className="social-brand instagram"
                    target="_blank"
                    rel="noreferrer"
                    href={instagramUrl}
                    aria-label="Instagram"
                  >
                    <InstaIcon />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Certification strip (e.g. CIDB) */}
      {certLogoUrl && (
        <div
          style={{
            borderTop: '1px solid rgba(0,0,0,0.08)',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <img
            src={
              certLogoUrl.startsWith('http')
                ? certLogoUrl + '?h=200&auto=format'
                : certLogoUrl
            }
            alt={certTitle || 'CIDB Malaysia'}
            loading="lazy"
            style={{
              height: '80px',
              width: 'auto',
              maxWidth: '100%',
              display: 'block',
            }}
          />
          {certTitle && (
            <p
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '12px',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'rgba(0,0,0,0.5)',
                textAlign: 'center',
              }}
            >
              {certTitle}
            </p>
          )}
        </div>
      )}

      {/* Bottom bar */}
      <div
        style={{
          borderTop: '1px solid rgba(0,0,0,0.12)',
          padding: '20px 24px',
          textAlign: 'center',
        }}
      >
        <p
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '13px',
            color: 'rgba(0,0,0,0.55)',
          }}
        >
          &copy; {new Date().getFullYear()}{' '}
          <span className="brand-decorzo">Decorzo</span>{' '}
          <span className="brand-space">Space</span>. All Rights Reserved.
        </p>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .grid-footer { grid-template-columns: 1fr 1fr !important; }
          .footer-quicklinks { padding-left: 0 !important; }
        }
        @media (max-width: 540px) {
          .grid-footer { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </footer>
  );
}
